// Phase 27.30.2 — Event time formatters that respect the event's own
// IANA timezone AND surface an abbreviation (e.g. "ET", "PT") so an
// agent traveling across timezones can never confuse the display time
// with their browser's local time.
//
// Every event carries a `timezone` field (IANA string, e.g.
// "America/New_York"). All formatters take that alongside the ISO
// datetime and render in that zone. Falls back to the browser's zone
// only when the event has no timezone stored.

const _abbrevCache = new Map();

/**
 * Return a short timezone abbreviation for a given IANA zone at a given
 * moment (DST-aware). "America/New_York" → "EST" / "EDT". Cached by
 * `${tz}|${date}` so repeated renders stay cheap.
 */
export function tzAbbrev(iso, tz) {
  if (!tz) return '';
  const key = `${tz}|${iso || ''}`;
  if (_abbrevCache.has(key)) return _abbrevCache.get(key);
  try {
    const d = iso ? new Date(iso) : new Date();
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short',
    }).formatToParts(d);
    const found = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    _abbrevCache.set(key, found);
    return found;
  } catch {
    return '';
  }
}

/**
 * Format an event start (or any ISO datetime) as a compact wall-clock
 * time in the event's own timezone, with the tz abbreviation appended.
 * Example: "9:30 AM EDT".
 */
export function formatEventTime(iso, tz, options = {}) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const time = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: tz || undefined,
      ...options,
    });
    const abbr = tz ? tzAbbrev(iso, tz) : '';
    return abbr ? `${time} ${abbr}` : time;
  } catch {
    return iso;
  }
}

/**
 * Format an event date+time as a single string in the event's own
 * timezone with tz abbreviation. Example: "Aug 10, 2026, 9:30 AM EDT".
 */
export function formatEventDateTime(iso, tz) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const dt = d.toLocaleString('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: tz || undefined,
    });
    const abbr = tz ? tzAbbrev(iso, tz) : '';
    return abbr ? `${dt} ${abbr}` : dt;
  } catch {
    return iso;
  }
}

/** Date-only string ("Aug 10, 2026") in the event's timezone. */
export function formatEventDate(iso, tz) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: tz || undefined,
    });
  } catch {
    return iso;
  }
}
